export type OperationType =
  | 'text_to_video'
  | 'image_to_video'
  | 'first_last_frame'
  | 'multiple_images';

export interface ModelConfig {
  id: string;
  name: string;
  brand: string;
  brandIcon: string;
  tag: string;
  description: string;
  pro: boolean;
  operations: OperationType[];
  durations: string[];
  aspectRatios: string[];
  resolutions: string[];
  defaultDuration: string;
  defaultAspectRatio: string;
  defaultResolution: string;
  supportsNegativePrompt: boolean;
  supportsSeed: boolean;
  supportsAudio: boolean;
  supportsAutoFix: boolean;
  /** V2-only models carry their own fal endpoint ids (V1 models resolve via
   *  the shared V1 config). The backend passes fal_model straight through. */
  falModels?: Partial<Record<OperationType, string>>;
  /** V2-only models: OUR credit rate per second by resolution. */
  creditsPerSecond?: Record<string, number>;
  /** Curated visual-style presets (fal `style` enum). Shown as a picker in
   *  Advanced settings. value = the exact fal enum string; '' = model default. */
  styleOptions?: { value: string; label: string }[];
  /** Vidu `movement_amplitude` — motion-intensity control (auto/small/medium/large). */
  supportsMotion?: boolean;
  /** Kling `cfg_scale` — prompt-adherence control (0–1). Surfaced as Loose/Balanced/Strict. */
  supportsCfgScale?: boolean;
  /** Kling o3/v3 `shot_type` — 'customize' keeps one continuous shot, 'intelligent'
   *  lets Kling cut the clip into multiple shots itself. No pricing impact. */
  supportsShotType?: boolean;
  /** Paid orders go through POST /v2-create-order (per-model fal payload
   *  schemas) instead of the live create-order generic payload. */
  v2Endpoint?: boolean;
  /** Kept for lookups (existing orders, pricing) but not offered in the manual picker. */
  hidden?: boolean;
}

export const FREE_MODEL_ID = 'veo31_fast';

export const MODEL_CONFIGS: ModelConfig[] = [
  {
    id: 'veo31_fast',
    // Id/endpoints stay Veo: the backend runs these on Gemini Omni Flash (Veo retired 2026-10-22).
    name: 'Gemini Omni Flash',
    brand: 'Google',
    brandIcon: 'https://www.google.com/favicon.ico',
    tag: 'Fast & Cheap',
    description: "Google's fast video model. 720p, 1080p or 4K, native audio, first and last frame and reference images.",
    pro: false,
    operations: ['text_to_video', 'image_to_video', 'first_last_frame', 'multiple_images'],
    durations: ['4s', '6s', '8s'],
    aspectRatios: ['16:9', '9:16'],
    resolutions: ['720p', '1080p', '4k'],
    defaultDuration: '4s',
    defaultAspectRatio: '16:9',
    defaultResolution: '720p',
    supportsNegativePrompt: false,
    supportsSeed: false,
    supportsAudio: false,
    supportsAutoFix: false,
    // fal endpoints from V1; the backend maps each to Gemini Omni Flash.
    falModels: {
      text_to_video: 'fal-ai/veo3.1/fast',
      image_to_video: 'fal-ai/veo3.1/fast/image-to-video',
      first_last_frame: 'fal-ai/veo3.1/fast/first-last-frame-to-video',
      multiple_images: 'fal-ai/veo3.1/reference-to-video',
    },
    creditsPerSecond: { '720p': 2, '1080p': 2, '4k': 4 },
    v2Endpoint: true,
  },
  {
    id: 'seedance25',
    name: 'Seedance 2.5',
    brand: 'ByteDance',
    brandIcon: 'https://www.google.com/s2/favicons?domain=seed.bytedance.com&sz=64',
    tag: 'New · Flagship · Native audio',
    description: "ByteDance's newest flagship — state-of-the-art motion & realism, ~20% sharper prompt adherence, and native audio generated jointly with the video. Up to 30s in a single shot. Premium quality, premium cost.",
    pro: true,
    operations: ['text_to_video', 'image_to_video', 'first_last_frame'],
    // Full fal duration enum ("4"–"30", verified Aug 2026). fal also has "auto"
    // (model picks the length) — NOT offered: the charge must be known up-front.
    durations: [
      '4s', '5s', '6s', '7s', '8s', '9s', '10s', '11s', '12s', '13s', '14s',
      '15s', '16s', '17s', '18s', '19s', '20s', '21s', '22s', '23s', '24s',
      '25s', '26s', '27s', '28s', '29s', '30s',
    ],
    aspectRatios: ['21:9', '16:9', '4:3', '1:1', '3:4', '9:16'],
    resolutions: ['480p', '720p'],
    defaultDuration: '5s',
    defaultAspectRatio: '16:9',
    defaultResolution: '720p',
    supportsNegativePrompt: false,
    supportsSeed: true,
    supportsAudio: true,
    supportsAutoFix: false,
    // fal slug (NO fal-ai/ prefix, like seedance-2.0). i2v takes image_url + optional
    // end_image_url (first→last). Native audio. 480p/720p only (no 1080p/4K).
    falModels: {
      text_to_video: 'bytedance/seedance-2.5/text-to-video',
      image_to_video: 'bytedance/seedance-2.5/image-to-video',
      first_last_frame: 'bytedance/seedance-2.5/image-to-video', // image_url + optional end_image_url
    },
    // fal (16:9): ~$0.4730/s @720p, ~$0.2205/s @480p — pricier than 2.0. SAME margin
    // method as every model (worst-case MAX-yearly ≈ $0.038/credit): ceil($/s ÷ 0.038)+1
    // → 720p ceil(12.45)+1=14, 480p ceil(5.80)+1=7.
    creditsPerSecond: { '480p': 7, '720p': 14 },
    v2Endpoint: true,
  },
  {
    id: 'seedance2',
    name: 'Seedance 2.0',
    brand: 'ByteDance',
    brandIcon: 'https://www.google.com/s2/favicons?domain=seed.bytedance.com&sz=64',
    tag: 'New · SOTA · Premium',
    description: "ByteDance's brand-new flagship — state-of-the-art motion, realism and native audio. Up to 15s in a single shot. Premium quality, premium cost.",
    pro: true,
    operations: ['text_to_video', 'image_to_video', 'first_last_frame'],
    // Full fal duration enum ("4"–"15", verified Aug 2026); "auto" not offered
    // (charge must be known up-front).
    durations: [
      '4s', '5s', '6s', '7s', '8s', '9s', '10s', '11s', '12s', '13s', '14s', '15s',
    ],
    aspectRatios: ['21:9', '16:9', '4:3', '1:1', '3:4', '9:16'],
    resolutions: ['480p', '720p'],
    defaultDuration: '5s',
    defaultAspectRatio: '16:9',
    defaultResolution: '720p',
    supportsNegativePrompt: false,
    supportsSeed: true,
    supportsAudio: true,
    supportsAutoFix: false,
    // fal slug verified Apr 2026: NO fal-ai/ prefix, NO /pro. Duration is a string; native audio.
    falModels: {
      text_to_video: 'bytedance/seedance-2.0/text-to-video',
      image_to_video: 'bytedance/seedance-2.0/image-to-video',
      first_last_frame: 'bytedance/seedance-2.0/image-to-video', // image_url + optional end_image_url
    },
    // ~$0.303/s @720p on fal (very costly). Priced to keep margin at EVERY credit tier
    // (worst case MAX-yearly ≈ $0.038/credit: 9·0.038=$0.342 > $0.303). 480p ≈ $0.135/s.
    creditsPerSecond: { '480p': 5, '720p': 9 },
    v2Endpoint: true,
  },
  {
    id: 'kling25_turbo',
    name: 'Kling 2.5 Turbo Pro',
    brand: 'Kuaishou',
    brandIcon: 'https://klingai.com/favicon.ico',
    tag: 'New · Cinematic motion',
    description: 'Top-tier motion fluidity and prompt precision at a great price.',
    pro: true,
    operations: ['text_to_video', 'image_to_video', 'first_last_frame'],
    durations: ['5s', '10s'],
    aspectRatios: ['16:9', '9:16', '1:1'],
    resolutions: ['1080p'],
    defaultDuration: '5s',
    defaultAspectRatio: '16:9',
    defaultResolution: '1080p',
    supportsNegativePrompt: true,
    supportsSeed: false,
    supportsAudio: false,
    supportsAutoFix: false,
    // fal endpoint verified live on fal.ai (June 2026); backend passes through.
    falModels: {
      text_to_video: 'fal-ai/kling-video/v2.5-turbo/pro/text-to-video',
      image_to_video: 'fal-ai/kling-video/v2.5-turbo/pro/image-to-video',
      // first/last rides the i2v endpoint (image_url + tail_image_url).
      first_last_frame: 'fal-ai/kling-video/v2.5-turbo/pro/image-to-video',
    },
    creditsPerSecond: { '1080p': 2 },
    supportsCfgScale: true, // fal cfg_scale (default 0.5), verified live
    v2Endpoint: true,
  },
  {
    id: 'hailuo02_pro',
    name: 'Hailuo 02 Pro',
    brand: 'MiniMax',
    brandIcon: 'https://www.minimax.io/favicon.ico',
    tag: 'New · Real-world physics',
    description: 'Stunningly lifelike motion and physics. Fixed 6s clips in crisp 1080p.',
    pro: true,
    operations: ['text_to_video', 'image_to_video'],
    durations: ['6s'],
    aspectRatios: ['16:9'],
    resolutions: ['1080p'],
    defaultDuration: '6s',
    defaultAspectRatio: '16:9',
    defaultResolution: '1080p',
    supportsNegativePrompt: false,
    supportsSeed: false,
    supportsAudio: false,
    supportsAutoFix: false,
    falModels: {
      text_to_video: 'fal-ai/minimax/hailuo-02/pro/text-to-video',
      image_to_video: 'fal-ai/minimax/hailuo-02/pro/image-to-video',
    },
    creditsPerSecond: { '1080p': 2 },
    v2Endpoint: true,
  },
  {
    id: 'pixverse_v5',
    name: 'PixVerse V5',
    brand: 'PixVerse',
    // app.pixverse.ai/favicon.ico 404s — Google's favicon service resolves it.
    brandIcon: 'https://www.google.com/s2/favicons?domain=app.pixverse.ai&sz=64',
    tag: 'New · Cheapest, anime & styles',
    description: 'Best value in the lineup. Punchy stylized looks — anime, 3D, comic — from 1 credit/s.',
    pro: true,
    operations: ['text_to_video', 'image_to_video'],
    durations: ['5s', '8s'],
    aspectRatios: ['16:9', '4:3', '1:1', '3:4', '9:16'],
    resolutions: ['360p', '540p', '720p', '1080p'],
    defaultDuration: '5s',
    defaultAspectRatio: '16:9',
    defaultResolution: '720p',
    supportsNegativePrompt: true,
    supportsSeed: true,
    supportsAudio: false,
    supportsAutoFix: false,
    falModels: {
      text_to_video: 'fal-ai/pixverse/v5/text-to-video',
      image_to_video: 'fal-ai/pixverse/v5/image-to-video',
    },
    creditsPerSecond: { '360p': 1, '540p': 1, '720p': 1, '1080p': 2 },
    // Verified live against the fal `style` enum (June 2026). '' = no style preset.
    styleOptions: [
      { value: '', label: 'Default' },
      { value: 'anime', label: 'Anime' },
      { value: '3d_animation', label: '3D' },
      { value: 'clay', label: 'Clay' },
      { value: 'comic', label: 'Comic' },
      { value: 'cyberpunk', label: 'Cyberpunk' },
    ],
    v2Endpoint: true,
  },
  {
    id: 'seedance_pro',
    name: 'Seedance 1.0 Pro',
    brand: 'ByteDance',
    // bytedance.com serves HTML at /favicon.ico — use the Seed team icon.
    brandIcon: 'https://www.google.com/s2/favicons?domain=seed.bytedance.com&sz=64',
    tag: 'New · Ultra-wide 21:9',
    description: 'Cinema-grade multi-shot storytelling, 2–12s, the only model with ultra-wide 21:9.',
    pro: true,
    operations: ['text_to_video', 'image_to_video', 'first_last_frame'],
    // Full fal duration enum ("2"–"12", verified Aug 2026).
    durations: ['2s', '3s', '4s', '5s', '6s', '7s', '8s', '9s', '10s', '11s', '12s'],
    aspectRatios: ['21:9', '16:9', '4:3', '1:1', '3:4', '9:16'],
    resolutions: ['480p', '720p', '1080p'],
    defaultDuration: '5s',
    defaultAspectRatio: '16:9',
    defaultResolution: '1080p',
    supportsNegativePrompt: false,
    supportsSeed: true,
    supportsAudio: false,
    supportsAutoFix: false,
    falModels: {
      text_to_video: 'fal-ai/bytedance/seedance/v1/pro/text-to-video',
      image_to_video: 'fal-ai/bytedance/seedance/v1/pro/image-to-video',
      // first/last frame rides the i2v endpoint (image_url + end_image_url).
      first_last_frame: 'fal-ai/bytedance/seedance/v1/pro/image-to-video',
    },
    creditsPerSecond: { '480p': 2, '720p': 2, '1080p': 3 },
    v2Endpoint: true,
  },
  {
    id: 'wan25',
    name: 'Wan 2.5',
    brand: 'Alibaba',
    brandIcon: 'https://www.google.com/s2/favicons?domain=wan.video&sz=64',
    tag: 'New · Native audio',
    description: "Alibaba's Wan 2.5 — crisp motion with synced native audio, 5–10s up to 1080p.",
    pro: true,
    operations: ['text_to_video', 'image_to_video'],
    durations: ['5s', '10s'],
    aspectRatios: ['16:9', '9:16', '1:1'],
    resolutions: ['480p', '720p', '1080p'],
    defaultDuration: '5s',
    defaultAspectRatio: '16:9',
    defaultResolution: '1080p',
    supportsNegativePrompt: true,
    supportsSeed: true,
    supportsAudio: false,
    supportsAutoFix: false,
    // fal endpoint + input schema verified on fal.ai (June 2026).
    falModels: {
      text_to_video: 'fal-ai/wan-25-preview/text-to-video',
      image_to_video: 'fal-ai/wan-25-preview/image-to-video',
    },
    creditsPerSecond: { '480p': 1, '720p': 1, '1080p': 2 },
    v2Endpoint: true,
  },
  {
    id: 'ltx2',
    name: 'LTX-2 Pro',
    brand: 'Lightricks',
    brandIcon: 'https://www.google.com/s2/favicons?domain=ltx.studio&sz=64',
    tag: 'New · Up to 4K + audio',
    description: 'Lightricks LTX-2 — fast, affordable video up to 4K (2160p) with native audio.',
    pro: true,
    operations: ['text_to_video', 'image_to_video'],
    durations: ['6s', '8s', '10s'],
    aspectRatios: ['16:9'],
    resolutions: ['1080p', '1440p', '2160p'],
    defaultDuration: '6s',
    defaultAspectRatio: '16:9',
    defaultResolution: '1080p',
    supportsNegativePrompt: false,
    supportsSeed: false,
    supportsAudio: true,
    supportsAutoFix: false,
    falModels: {
      text_to_video: 'fal-ai/ltx-2/text-to-video',
      image_to_video: 'fal-ai/ltx-2/image-to-video',
    },
    creditsPerSecond: { '1080p': 1, '1440p': 2, '2160p': 3 },
    v2Endpoint: true,
  },
  {
    id: 'hunyuan',
    name: 'Hunyuan Video',
    brand: 'Tencent',
    brandIcon: 'https://www.google.com/s2/favicons?domain=tencent.com&sz=64',
    tag: 'New · Open source',
    description: "Tencent's open-source Hunyuan — expressive ~5s clips with great motion.",
    pro: true,
    operations: ['text_to_video'],
    durations: ['5s'],
    aspectRatios: ['16:9', '9:16'],
    resolutions: ['480p', '580p', '720p'],
    defaultDuration: '5s',
    defaultAspectRatio: '16:9',
    defaultResolution: '720p',
    supportsNegativePrompt: false,
    supportsSeed: true,
    supportsAudio: false,
    supportsAutoFix: false,
    falModels: {
      text_to_video: 'fal-ai/hunyuan-video',
    },
    creditsPerSecond: { '480p': 1, '580p': 1, '720p': 1 },
    v2Endpoint: true,
  },
  {
    id: 'luma_ray2',
    name: 'Luma Ray 2',
    brand: 'Luma AI',
    brandIcon: 'https://www.google.com/s2/favicons?domain=lumalabs.ai&sz=64',
    tag: 'New · Ultra-wide 21:9',
    description: "Luma's Ray 2 — fluid, cinematic motion with the widest aspect range (up to 21:9).",
    pro: true,
    operations: ['text_to_video', 'image_to_video', 'first_last_frame'],
    durations: ['5s', '9s'],
    aspectRatios: ['16:9', '9:16', '4:3', '3:4', '21:9', '9:21'],
    resolutions: ['540p', '720p', '1080p'],
    defaultDuration: '5s',
    defaultAspectRatio: '16:9',
    defaultResolution: '720p',
    supportsNegativePrompt: false,
    supportsSeed: false,
    supportsAudio: false,
    supportsAutoFix: false,
    falModels: {
      text_to_video: 'fal-ai/luma-dream-machine/ray-2',
      image_to_video: 'fal-ai/luma-dream-machine/ray-2/image-to-video',
      // first/last rides the i2v endpoint (image_url + end_image_url).
      first_last_frame: 'fal-ai/luma-dream-machine/ray-2/image-to-video',
    },
    creditsPerSecond: { '540p': 1, '720p': 2, '1080p': 4 },
    v2Endpoint: true,
  },
  {
    id: 'pika22',
    name: 'Pika 2.2',
    brand: 'Pika',
    brandIcon: 'https://www.google.com/s2/favicons?domain=pika.art&sz=64',
    tag: 'New · Lots of aspect ratios',
    description: 'Pika 2.2 — punchy, stylized motion with the most aspect-ratio options.',
    pro: true,
    operations: ['text_to_video', 'image_to_video'],
    durations: ['5s', '10s'],
    aspectRatios: ['16:9', '9:16', '1:1', '4:5', '5:4', '3:2', '2:3'],
    resolutions: ['720p', '1080p'],
    defaultDuration: '5s',
    defaultAspectRatio: '16:9',
    defaultResolution: '720p',
    supportsNegativePrompt: true,
    supportsSeed: true,
    supportsAudio: false,
    supportsAutoFix: false,
    falModels: {
      text_to_video: 'fal-ai/pika/v2.2/text-to-video',
      image_to_video: 'fal-ai/pika/v2.2/image-to-video',
    },
    creditsPerSecond: { '720p': 1, '1080p': 2 },
    v2Endpoint: true,
  },
  {
    id: 'vidu_q1',
    name: 'Vidu Q1',
    brand: 'Vidu',
    brandIcon: 'https://www.google.com/s2/favicons?domain=vidu.com&sz=64',
    tag: 'New · Anime & general',
    description: 'Vidu Q1 — crisp ~5s clips with a dedicated anime style. Fast and affordable.',
    pro: true,
    operations: ['text_to_video'],
    durations: ['5s'],
    aspectRatios: ['16:9', '9:16', '1:1'],
    resolutions: ['1080p'],
    defaultDuration: '5s',
    defaultAspectRatio: '16:9',
    defaultResolution: '1080p',
    supportsNegativePrompt: false,
    supportsSeed: true,
    supportsAudio: false,
    supportsAutoFix: false,
    falModels: {
      text_to_video: 'fal-ai/vidu/q1/text-to-video',
    },
    creditsPerSecond: { '1080p': 1 },
    // Verified live against the fal schema (style enum + movement_amplitude).
    styleOptions: [
      { value: 'general', label: 'General' },
      { value: 'anime', label: 'Anime' },
    ],
    supportsMotion: true,
    v2Endpoint: true,
  },
  {
    id: 'kling21_master',
    name: 'Kling 2.1 Master',
    brand: 'Kuaishou',
    brandIcon: 'https://klingai.com/favicon.ico',
    tag: 'New · Premium quality',
    description: "Kling's top 2.1 tier — premium realism and prompt precision, 1080p.",
    pro: true,
    operations: ['text_to_video', 'image_to_video'],
    durations: ['5s', '10s'],
    aspectRatios: ['16:9', '9:16', '1:1'],
    resolutions: ['1080p'],
    defaultDuration: '5s',
    defaultAspectRatio: '16:9',
    defaultResolution: '1080p',
    supportsNegativePrompt: true,
    supportsSeed: false,
    supportsAudio: false,
    supportsAutoFix: false,
    falModels: {
      text_to_video: 'fal-ai/kling-video/v2.1/master/text-to-video',
      image_to_video: 'fal-ai/kling-video/v2.1/master/image-to-video',
    },
    creditsPerSecond: { '1080p': 3 },
    supportsCfgScale: true, // fal cfg_scale (default 0.5), verified live
    v2Endpoint: true,
  },
  {
    id: 'kling21_standard',
    name: 'Kling 2.1 Standard',
    brand: 'Kuaishou',
    brandIcon: 'https://klingai.com/favicon.ico',
    tag: 'New · Great value',
    description: 'Kling 2.1 Standard — the same engine at a friendlier price, 1080p.',
    pro: true,
    // IMAGE-TO-VIDEO ONLY. fal has no v2.1/standard/text-to-video endpoint — it
    // 404s. Text-to-video on the 2.1 line exists only on the Master tier.
    operations: ['image_to_video'],
    durations: ['5s', '10s'],
    aspectRatios: ['16:9', '9:16', '1:1'],
    resolutions: ['1080p'],
    defaultDuration: '5s',
    defaultAspectRatio: '16:9',
    defaultResolution: '1080p',
    supportsNegativePrompt: true,
    supportsSeed: false,
    supportsAudio: false,
    supportsAutoFix: false,
    falModels: {
      image_to_video: 'fal-ai/kling-video/v2.1/standard/image-to-video',
    },
    creditsPerSecond: { '1080p': 2 },
    v2Endpoint: true,
  },
  {
    id: 'marey',
    name: 'Marey Realism',
    brand: 'Moonvalley',
    brandIcon: 'https://www.google.com/s2/favicons?domain=moonvalley.com&sz=64',
    tag: 'New · Cinematic realism',
    description: "Moonvalley's Marey — film-grade realism, licensed and production-ready.",
    pro: true,
    operations: ['text_to_video'],
    durations: ['5s', '10s'],
    aspectRatios: ['16:9', '1:1', '4:3', '3:4'],
    resolutions: ['1080p'],
    defaultDuration: '5s',
    defaultAspectRatio: '16:9',
    defaultResolution: '1080p',
    supportsNegativePrompt: true,
    supportsSeed: true,
    supportsAudio: false,
    supportsAutoFix: false,
    falModels: {
      text_to_video: 'moonvalley/marey/t2v',
    },
    creditsPerSecond: { '1080p': 3 },
    v2Endpoint: true,
  },
  {
    id: 'cogvideox',
    name: 'CogVideoX 5B',
    brand: 'Zhipu AI',
    brandIcon: 'https://www.google.com/s2/favicons?domain=zhipuai.cn&sz=64',
    tag: 'New · Open source, cheap',
    description: 'CogVideoX-5B — open-source, budget-friendly video for quick ideas.',
    pro: true,
    operations: ['text_to_video'],
    durations: ['6s'],
    aspectRatios: ['16:9', '9:16', '1:1', '4:3', '3:4'],
    resolutions: ['720p'],
    defaultDuration: '6s',
    defaultAspectRatio: '16:9',
    defaultResolution: '720p',
    supportsNegativePrompt: true,
    supportsSeed: true,
    supportsAudio: false,
    supportsAutoFix: false,
    falModels: {
      text_to_video: 'fal-ai/cogvideox-5b',
    },
    creditsPerSecond: { '720p': 0.5 },
    v2Endpoint: true,
  },
  {
    id: 'hailuo23',
    name: 'Hailuo 2.3',
    brand: 'MiniMax',
    brandIcon: 'https://www.minimax.io/favicon.ico',
    tag: 'New · Lifelike motion',
    description: "MiniMax's newest Hailuo 2.3 — lifelike physics and motion, 6–10s.",
    pro: true,
    operations: ['text_to_video', 'image_to_video'],
    durations: ['6s', '10s'],
    aspectRatios: ['16:9'],
    resolutions: ['768p'],
    defaultDuration: '6s',
    defaultAspectRatio: '16:9',
    defaultResolution: '768p',
    supportsNegativePrompt: false,
    supportsSeed: false,
    supportsAudio: false,
    supportsAutoFix: false,
    falModels: {
      text_to_video: 'fal-ai/minimax/hailuo-2.3/standard/text-to-video',
      image_to_video: 'fal-ai/minimax/hailuo-2.3/standard/image-to-video',
    },
    creditsPerSecond: { '768p': 1 },
    v2Endpoint: true,
  },
  {
    id: 'veo31',
    // Id/endpoints stay Veo: the backend runs these on MiniMax H3 Max (Veo retired 2026-10-22).
    name: 'H3 Max',
    brand: 'MiniMax',
    brandIcon: 'https://www.minimax.io/favicon.ico',
    tag: 'Best Quality',
    description: "MiniMax's high-quality model. Up to 1080p, native audio, seed control.",
    pro: true,
    // No Multiple Images: the backend runs reference on Gemini Omni Flash (veo31_fast).
    operations: ['text_to_video', 'image_to_video', 'first_last_frame'],
    durations: ['4s', '6s', '8s'],
    aspectRatios: ['16:9', '9:16'],
    resolutions: ['720p', '1080p'],
    defaultDuration: '8s',
    defaultAspectRatio: '16:9',
    defaultResolution: '720p',
    supportsNegativePrompt: false,
    supportsSeed: true,
    supportsAudio: false,
    supportsAutoFix: false,
    // Full-tier Veo ids; the backend maps t2v/i2v/first-last to H3 Max (reference to Gemini).
    falModels: {
      text_to_video: 'fal-ai/veo3.1',
      image_to_video: 'fal-ai/veo3.1/image-to-video',
      first_last_frame: 'fal-ai/veo3.1/first-last-frame-to-video',
      multiple_images: 'fal-ai/veo3.1/reference-to-video',
    },
    creditsPerSecond: { '720p': 4, '1080p': 4 },
    v2Endpoint: true,
  },
  {
    id: 'sora2',
    name: 'Sora 2',
    brand: 'OpenAI',
    brandIcon: 'https://openai.com/favicon.ico',
    tag: 'Creative',
    description: "OpenAI's creative model. Up to 20s with native audio.",
    pro: true,
    operations: ['text_to_video', 'image_to_video'],
    // Full fal duration enum ({4,8,12,16,20}, verified Aug 2026).
    durations: ['4s', '8s', '12s', '16s', '20s'],
    aspectRatios: ['16:9', '9:16'],
    resolutions: ['720p'],
    defaultDuration: '8s',
    defaultAspectRatio: '16:9',
    defaultResolution: '720p',
    supportsNegativePrompt: false,
    supportsSeed: false,
    supportsAudio: false,
    supportsAutoFix: false,
    // OpenAI Sora 2 — native audio (always on); duration sent as an integer server-side.
    falModels: {
      text_to_video: 'fal-ai/sora-2/text-to-video',
      image_to_video: 'fal-ai/sora-2/image-to-video',
    },
    creditsPerSecond: { '720p': 3 },
    v2Endpoint: true,
    hidden: true,
  },
  {
    id: 'sora2_pro',
    name: 'Sora 2 Pro',
    brand: 'OpenAI',
    brandIcon: 'https://openai.com/favicon.ico',
    tag: '1080p',
    description: 'Higher quality Sora 2 with 1080p support. Up to 20s.',
    pro: true,
    operations: ['text_to_video', 'image_to_video'],
    // Full fal duration enum ({4,8,12,16,20}, verified Aug 2026).
    durations: ['4s', '8s', '12s', '16s', '20s'],
    aspectRatios: ['16:9', '9:16'],
    resolutions: ['720p', '1080p'],
    defaultDuration: '8s',
    defaultAspectRatio: '16:9',
    defaultResolution: '1080p',
    supportsNegativePrompt: false,
    supportsSeed: false,
    supportsAudio: false,
    supportsAutoFix: false,
    // Sora 2 Pro — adds 1080p; native audio. /pro path suffix on the same model.
    falModels: {
      text_to_video: 'fal-ai/sora-2/text-to-video/pro',
      image_to_video: 'fal-ai/sora-2/image-to-video/pro',
    },
    creditsPerSecond: { '720p': 5, '1080p': 5 },
    v2Endpoint: true,
    hidden: true,
  },
  {
    id: 'grok',
    name: 'Grok Video',
    brand: 'xAI',
    brandIcon: 'https://x.ai/favicon.ico',
    tag: 'Audio + 15s',
    description: "xAI's video model. Up to 15s with native audio.",
    pro: true,
    operations: ['text_to_video', 'image_to_video'],
    // fal takes any integer 1–15 (verified Aug 2026); offered in full.
    durations: [
      '1s', '2s', '3s', '4s', '5s', '6s', '7s', '8s', '9s', '10s',
      '11s', '12s', '13s', '14s', '15s',
    ],
    aspectRatios: ['16:9', '4:3', '3:2', '1:1', '2:3', '3:4', '9:16'],
    resolutions: ['480p', '720p'],
    defaultDuration: '6s',
    defaultAspectRatio: '16:9',
    defaultResolution: '720p',
    supportsNegativePrompt: false,
    supportsSeed: false,
    supportsAudio: true,
    supportsAutoFix: false,
    // xAI Grok Imagine — native audio; max 15s; integer duration server-side.
    falModels: {
      text_to_video: 'xai/grok-imagine-video/text-to-video',
      image_to_video: 'xai/grok-imagine-video/image-to-video',
    },
    creditsPerSecond: { '480p': 1, '720p': 1 },
    v2Endpoint: true,
  },
  {
    id: 'wan26',
    name: 'WAN 2.6',
    brand: 'Wan AI',
    brandIcon: 'https://wan.video/favicon.ico',
    tag: 'Multi-shot + 1080p',
    description: "Alibaba's WAN 2.6 model. Multi-shot support, 1080p, up to 15s.",
    pro: true,
    operations: ['text_to_video', 'image_to_video'],
    durations: ['5s', '10s', '15s'],
    aspectRatios: ['16:9', '9:16', '1:1', '4:3', '3:4'],
    resolutions: ['720p', '1080p'],
    defaultDuration: '5s',
    defaultAspectRatio: '16:9',
    defaultResolution: '1080p',
    supportsNegativePrompt: true,
    supportsSeed: true,
    supportsAudio: false,
    supportsAutoFix: false,
    // Alibaba WAN 2.6 — duration string server-side; no native audio synthesis.
    falModels: {
      text_to_video: 'wan/v2.6/text-to-video',
      image_to_video: 'wan/v2.6/image-to-video',
    },
    creditsPerSecond: { '720p': 1, '1080p': 2 },
    v2Endpoint: true,
  },
  {
    id: 'kling_o3',
    name: 'Kling O3',
    brand: 'Kling',
    brandIcon: 'https://klingai.com/favicon.ico',
    tag: 'Up to 15s',
    description: 'Newest Kling (3.0). Up to 15s, 1:1 support, native audio.',
    pro: true,
    // All four modes. first_last_frame rides the i2v endpoint (image_url +
    // end_image_url); multiple_images uses the dedicated reference endpoint.
    operations: ['text_to_video', 'image_to_video', 'first_last_frame', 'multiple_images'],
    // Full fal duration enum ("3"–"15", verified Aug 2026).
    durations: [
      '3s', '4s', '5s', '6s', '7s', '8s', '9s', '10s', '11s', '12s', '13s', '14s', '15s',
    ],
    aspectRatios: ['16:9', '9:16', '1:1'],
    resolutions: ['720p'],
    defaultDuration: '5s',
    defaultAspectRatio: '16:9',
    defaultResolution: '720p',
    supportsNegativePrompt: false,
    supportsSeed: false,
    supportsAudio: true,
    supportsAutoFix: false,
    // Kling O3 Pro (3.0) — native audio; duration string "3"–"15" server-side.
    // No negative_prompt and no cfg_scale on this line (verified against its OpenAPI).
    supportsShotType: true,
    falModels: {
      text_to_video: 'fal-ai/kling-video/o3/pro/text-to-video',
      image_to_video: 'fal-ai/kling-video/o3/pro/image-to-video',
      first_last_frame: 'fal-ai/kling-video/o3/pro/image-to-video',
      multiple_images: 'fal-ai/kling-video/o3/pro/reference-to-video',
    },
    // Same $0.112/s basis as every other O3 mode, so the family rate is unchanged.
    creditsPerSecond: { '720p': 3 },
    v2Endpoint: true,
  },
  {
    id: 'kling_v3',
    name: 'Kling V3',
    brand: 'Kling',
    brandIcon: 'https://klingai.com/favicon.ico',
    tag: 'Up to 15s',
    description: 'Kling V3 (3.0 Pro) with negative prompt and multi-shot support.',
    pro: true,
    // No reference endpoint on the v3 line, so no multiple_images here — that
    // mode lives on Kling O3. first_last_frame rides i2v (start + end image).
    operations: ['text_to_video', 'image_to_video', 'first_last_frame'],
    // Full fal duration enum ("3"–"15", verified Aug 2026).
    durations: [
      '3s', '4s', '5s', '6s', '7s', '8s', '9s', '10s', '11s', '12s', '13s', '14s', '15s',
    ],
    aspectRatios: ['16:9', '9:16', '1:1'],
    resolutions: ['720p'],
    defaultDuration: '5s',
    defaultAspectRatio: '16:9',
    defaultResolution: '720p',
    supportsNegativePrompt: true,
    supportsSeed: false,
    supportsAudio: true,
    supportsAutoFix: false,
    // Kling V3 Pro (3.0) — native audio + negative prompt + cfg_scale + shot_type.
    supportsCfgScale: true,
    supportsShotType: true,
    falModels: {
      text_to_video: 'fal-ai/kling-video/v3/pro/text-to-video',
      image_to_video: 'fal-ai/kling-video/v3/pro/image-to-video',
      first_last_frame: 'fal-ai/kling-video/v3/pro/image-to-video',
    },
    creditsPerSecond: { '720p': 4 },
    v2Endpoint: true,
  },
  {
    id: 'hailuo23_pro',
    name: 'Hailuo 2.3 Pro',
    brand: 'MiniMax',
    brandIcon: 'https://www.minimax.io/favicon.ico',
    tag: 'New · 1080p lifelike',
    description: 'MiniMax Hailuo 2.3 Pro — top-tier lifelike physics and motion in crisp 1080p.',
    pro: true,
    operations: ['text_to_video', 'image_to_video'],
    durations: ['6s'],
    aspectRatios: ['16:9'],
    resolutions: ['1080p'],
    defaultDuration: '6s',
    defaultAspectRatio: '16:9',
    defaultResolution: '1080p',
    supportsNegativePrompt: false,
    supportsSeed: false,
    supportsAudio: false,
    supportsAutoFix: false,
    falModels: {
      text_to_video: 'fal-ai/minimax/hailuo-2.3/pro/text-to-video',
      image_to_video: 'fal-ai/minimax/hailuo-2.3/pro/image-to-video',
    },
    creditsPerSecond: { '1080p': 1 },
    v2Endpoint: true,
  },
  {
    id: 'hailuo02_standard',
    name: 'Hailuo 02 Standard',
    brand: 'MiniMax',
    brandIcon: 'https://www.minimax.io/favicon.ico',
    tag: 'New · Great value',
    description: 'MiniMax Hailuo 02 Standard — lifelike physics at a friendly price.',
    pro: true,
    operations: ['text_to_video', 'image_to_video'],
    // fal standard tier takes duration "6"/"10" (verified Aug 2026; pro stays fixed ~6s).
    durations: ['6s', '10s'],
    aspectRatios: ['16:9'],
    resolutions: ['768p'],
    defaultDuration: '6s',
    defaultAspectRatio: '16:9',
    defaultResolution: '768p',
    supportsNegativePrompt: false,
    supportsSeed: false,
    supportsAudio: false,
    supportsAutoFix: false,
    falModels: {
      text_to_video: 'fal-ai/minimax/hailuo-02/standard/text-to-video',
      image_to_video: 'fal-ai/minimax/hailuo-02/standard/image-to-video',
    },
    creditsPerSecond: { '768p': 1 },
    v2Endpoint: true,
  },
  {
    id: 'wan22',
    name: 'Wan 2.2',
    brand: 'Alibaba',
    brandIcon: 'https://www.google.com/s2/favicons?domain=wan.video&sz=64',
    tag: 'New · Cheap & fast',
    description: "Alibaba's Wan 2.2 — efficient, budget-friendly video generation up to 720p.",
    pro: true,
    operations: ['text_to_video', 'image_to_video'],
    // fal takes num_frames 17–161 (~16fps → 2–10s); backend maps seconds → frames.
    durations: ['2s', '3s', '4s', '5s', '6s', '7s', '8s', '9s', '10s'],
    aspectRatios: ['16:9', '9:16', '1:1'],
    resolutions: ['480p', '580p', '720p'],
    defaultDuration: '5s',
    defaultAspectRatio: '16:9',
    defaultResolution: '720p',
    supportsNegativePrompt: true,
    supportsSeed: true,
    supportsAudio: false,
    supportsAutoFix: false,
    falModels: {
      text_to_video: 'fal-ai/wan/v2.2-a14b/text-to-video',
      image_to_video: 'fal-ai/wan/v2.2-a14b/image-to-video',
    },
    creditsPerSecond: { '480p': 1, '580p': 1, '720p': 1 },
    v2Endpoint: true,
  },
  {
    id: 'ray2_flash',
    name: 'Luma Ray 2 Flash',
    brand: 'Luma AI',
    brandIcon: 'https://www.google.com/s2/favicons?domain=lumalabs.ai&sz=64',
    tag: 'New · Fast & affordable',
    description: "Luma's faster, cheaper Ray 2 — quick cinematic clips from text, image or frames.",
    pro: true,
    operations: ['text_to_video', 'image_to_video', 'first_last_frame'],
    durations: ['5s', '9s'],
    aspectRatios: ['16:9', '9:16', '4:3', '3:4', '21:9', '9:21'],
    resolutions: ['540p', '720p', '1080p'],
    defaultDuration: '5s',
    defaultAspectRatio: '16:9',
    defaultResolution: '540p',
    supportsNegativePrompt: false,
    supportsSeed: false,
    supportsAudio: false,
    supportsAutoFix: false,
    falModels: {
      text_to_video: 'fal-ai/luma-dream-machine/ray-2-flash',
      image_to_video: 'fal-ai/luma-dream-machine/ray-2-flash/image-to-video',
      first_last_frame: 'fal-ai/luma-dream-machine/ray-2-flash/image-to-video',
    },
    creditsPerSecond: { '540p': 1, '720p': 1, '1080p': 2 },
    v2Endpoint: true,
  },
  {
    id: 'seedance_fast',
    name: 'Seedance Pro Fast',
    brand: 'ByteDance',
    brandIcon: 'https://www.google.com/s2/favicons?domain=seed.bytedance.com&sz=64',
    tag: 'New · Faster & cheaper',
    description: 'A faster, cheaper Seedance Pro — quick multi-shot clips, 2–12s.',
    pro: true,
    operations: ['text_to_video', 'image_to_video'],
    // Full fal duration enum ("2"–"12", verified Aug 2026 — same as Seedance 1.0 Pro).
    durations: ['2s', '3s', '4s', '5s', '6s', '7s', '8s', '9s', '10s', '11s', '12s'],
    aspectRatios: ['21:9', '16:9', '4:3', '1:1', '3:4', '9:16'],
    resolutions: ['480p', '720p', '1080p'],
    defaultDuration: '5s',
    defaultAspectRatio: '16:9',
    defaultResolution: '720p',
    supportsNegativePrompt: false,
    supportsSeed: true,
    supportsAudio: false,
    supportsAutoFix: false,
    falModels: {
      text_to_video: 'fal-ai/bytedance/seedance/v1/pro/fast/text-to-video',
      image_to_video: 'fal-ai/bytedance/seedance/v1/pro/fast/image-to-video',
    },
    creditsPerSecond: { '480p': 1, '720p': 1, '1080p': 2 },
    v2Endpoint: true,
  },
  {
    id: 'pixverse_transition',
    name: 'PixVerse Transition',
    brand: 'PixVerse',
    brandIcon: 'https://www.google.com/s2/favicons?domain=app.pixverse.ai&sz=64',
    tag: 'New · First → last frame',
    description: 'Morph a start image into an end image — smooth, stylized branded transitions.',
    pro: true,
    operations: ['first_last_frame'],
    durations: ['5s', '8s'],
    aspectRatios: ['16:9', '4:3', '1:1', '3:4', '9:16'],
    resolutions: ['360p', '540p', '720p', '1080p'],
    defaultDuration: '5s',
    defaultAspectRatio: '16:9',
    defaultResolution: '720p',
    supportsNegativePrompt: true,
    supportsSeed: true,
    supportsAudio: false,
    supportsAutoFix: false,
    falModels: {
      first_last_frame: 'fal-ai/pixverse/v5/transition',
    },
    creditsPerSecond: { '360p': 1, '540p': 1, '720p': 1, '1080p': 2 },
    v2Endpoint: true,
  },
  {
    id: 'vidu_startend',
    name: 'Vidu Q1 Frames',
    brand: 'Vidu',
    brandIcon: 'https://www.google.com/s2/favicons?domain=vidu.com&sz=64',
    tag: 'New · First → last frame',
    description: 'Smooth 1080p transition between your start and end images.',
    pro: true,
    operations: ['first_last_frame'],
    durations: ['5s'],
    aspectRatios: ['16:9'],
    resolutions: ['1080p'],
    defaultDuration: '5s',
    defaultAspectRatio: '16:9',
    defaultResolution: '1080p',
    supportsNegativePrompt: false,
    supportsSeed: true,
    supportsAudio: false,
    supportsAutoFix: false,
    falModels: {
      first_last_frame: 'fal-ai/vidu/q1/start-end-to-video',
    },
    creditsPerSecond: { '1080p': 1 },
    v2Endpoint: true,
  },
  {
    id: 'vidu_reference',
    name: 'Vidu Q1 Reference',
    brand: 'Vidu',
    brandIcon: 'https://www.google.com/s2/favicons?domain=vidu.com&sz=64',
    tag: 'New · Up to 7 reference images',
    description: 'Keep a subject consistent across the video using multiple reference images.',
    pro: true,
    operations: ['multiple_images'],
    durations: ['5s'],
    aspectRatios: ['16:9', '9:16', '1:1'],
    resolutions: ['1080p'],
    defaultDuration: '5s',
    defaultAspectRatio: '16:9',
    defaultResolution: '1080p',
    supportsNegativePrompt: false,
    supportsSeed: true,
    supportsAudio: false,
    supportsAutoFix: false,
    falModels: {
      multiple_images: 'fal-ai/vidu/q1/reference-to-video',
    },
    creditsPerSecond: { '1080p': 1 },
    v2Endpoint: true,
  },
];

export const OPERATION_LABELS: Record<OperationType, string> = {
  text_to_video: 'Text to Video',
  image_to_video: 'Image to Video',
  first_last_frame: 'First / Last Frame',
  multiple_images: 'Multiple Images',
};

export function getModelById(id: string): ModelConfig | undefined {
  return MODEL_CONFIGS.find((m) => m.id === id);
}

/** Like getModelById, but undefined for models hidden from the manual picker. */
export function getSelectableModelById(id: string): ModelConfig | undefined {
  const m = getModelById(id);
  return m && !m.hidden ? m : undefined;
}

// Operations a model no longer offers, mapped to the model that now serves them.
const OPERATION_MOVED_TO: Record<string, Partial<Record<OperationType, string>>> = {
  veo31: { multiple_images: 'veo31_fast' },
};

/** The model to use for a (possibly stale, saved) model + operation pair. */
export function modelForOperation(model: ModelConfig, operation: OperationType): ModelConfig {
  const movedTo = OPERATION_MOVED_TO[model.id]?.[operation];
  return (movedTo && getModelById(movedTo)) || model;
}

export function getModelsForOperation(operation: OperationType): ModelConfig[] {
  return MODEL_CONFIGS.filter((m) => !m.hidden && m.operations.includes(operation));
}

/**
 * Credits exactly as v2-create-order will CHARGE them for a manual order.
 *
 * The reference-to-video endpoints (Multiple Images) ignore the picked
 * duration and bill at the ENDPOINT's rate, not the picker model's own
 * table — Veo refs lock to 8s at the full-Veo rate (4/s, 6/s at 4k) even
 * when the picker shows the Fast model, and Vidu Q1 refs are a flat 5s ×
 * 1/s. The old flat "25" display under-quoted the real 32/48 charge.
 * Mirrors credit_rate() + the payload builders in the v2-create-order
 * lambda; the mobile app ships the same logic (calculateChargedCredits).
 * Returns null for operations priced by the normal per-second math.
 */
export function chargedCreditsForOperation(
  model: ModelConfig,
  operation: OperationType,
  resolution: string,
): number | null {
  if (operation !== 'multiple_images') return null;
  const endpoint = (model.falModels?.multiple_images ?? '').toLowerCase();
  if (endpoint.includes('vidu/q1')) return 5;
  if (endpoint.includes('veo3.1')) return resolution === '4k' ? 48 : 32;
  return null;
}
